import {
  LogoutOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { history, useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Spin } from 'antd';
import { createStyles } from 'antd-style';
import React from 'react';
import { flushSync } from 'react-dom';
import { logoutAdsConsole } from '@/services/ads-console/auth';
import { clearAdsLoginData } from '@/services/ads-console/authStorage';
import { clearOperationSession } from '@/services/auth/session';
import { logoutDevAdmin } from '@/services/dev-admin/api';
import HeaderDropdown from '../HeaderDropdown';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  return <span className="anticon">{currentUser?.name}</span>;
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  children,
}) => {
  /**
   * 退出登录，并且将当前的 url 保存
   */
  const operationLoginOut = () => {
    clearOperationSession();
    clearAdsLoginData();
    const { search, pathname } = window.location;
    const urlParams = new URL(window.location.href).searchParams;
    const searchParams = new URLSearchParams({
      redirect: pathname + search,
    });
    /** 此方法会跳转到 redirect 参数所在的位置 */
    const redirect = urlParams.get('redirect');
    // Note: There may be security issues, please note
    if (window.location.pathname !== '/user/login' && !redirect) {
      history.replace({
        pathname: '/user/login',
        search: `mode=operation&${searchParams.toString()}`,
      });
    }
  };

  const managementLoginOut = async () => {
    try {
      await logoutDevAdmin();
    } catch {
      // Dev session is local-only for this temporary management mode.
    } finally {
      history.replace('/user/login?mode=management');
    }
  };

  const adsLoginOut = async () => {
    try {
      await logoutAdsConsole();
    } catch {
      // Ads console logout may fail when the token has already expired.
    } finally {
      clearAdsLoginData();
      history.replace('/user/login?mode=ads');
    }
  };

  const { styles } = useStyles();

  const { initialState, setInitialState } = useModel('@@initialState');

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      const loginMode = initialState?.currentUser?.loginMode;
      void (async () => {
        flushSync(() => {
          setInitialState((s) => ({ ...s, currentUser: undefined }));
        });
        if (loginMode === 'management') {
          await managementLoginOut();
          return;
        }
        if (loginMode === 'ads') {
          await adsLoginOut();
          return;
        }
        operationLoginOut();
      })();
      return;
    }
    if (key === 'profile') {
      history.push('/account/center');
    }
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;

  if (!currentUser?.name) {
    return loading;
  }

  const showProfile = currentUser.loginMode === 'operation' || !currentUser.loginMode;
  const menuItems = [
    ...(showProfile
      ? [
          {
            key: 'profile',
            icon: <UserOutlined />,
            label: '个人中心',
          },
          {
            type: 'divider' as const,
          },
        ]
      : []),
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  return (
    <HeaderDropdown
      menu={{
        selectedKeys: [],
        onClick: onMenuClick,
        items: menuItems,
      }}
    >
      {children}
    </HeaderDropdown>
  );
};
